/**
 * Seka realtime contract v1 — зеркало серверного модуля.
 * Sync with: server/src/contracts/sekaV1.js (строки команд и форма снимка должны совпадать).
 *
 * @module contracts/sekaV1
 */

/** Должен совпадать с server/src/contracts/sekaV1.js */
export const SEKA_SCHEMA_VERSION = 1;

/** Имена команд — совпадают с server/src/contracts/messages.js COMMANDS.GAME */
export const SEKA_GAME_COMMAND = Object.freeze({
  READY: 'game/ready',
  START: 'game/start',
  ACTION: 'game/action',
  SVARKA_CHOICE: 'game/svarka-choice',
  BUY_IN: 'game/buy-in',
  LEAVE: 'game/leave',
  HEARTBEAT: 'game/heartbeat',
  RESUME: 'game/resume',
  CHAT_SEND: 'game/chat-send',
  /** Armenian Belot: `data.command` → server `applyCommand`. */
  BELOT_APPLY: 'game/belot-apply',
  SEKA_GRACE_EXIT: 'game/seka-grace-exit',
});

/** Синоним серверного `SEKA_GAME_COMMANDS` — тот же набор строк команд. */
export const SEKA_GAME_COMMANDS = SEKA_GAME_COMMAND;

/** Событие снимка состояния */
export const SEKA_GAME_STATE_EVENT = 'game/state';

/** Одноразовое событие списания анте при старте руки. */
export const SEKA_GAME_ANTE_TAKEN_EVENT = 'game/ante-taken';

/** Личные карты только для текущего клиента (сервер не шлёт чужие в broadcast). */
export const SEKA_GAME_PRIVATE_HOLE_EVENT = 'game/private-hole';

/** Сообщение in-game чата, server-authoritative профиль автора. */
export const SEKA_GAME_CHAT_MESSAGE_EVENT = 'game/chat-message';

/** Аварийное завершение матча (синхронно с server `EVENTS.MATCH_ABORTED`). */
export const SEKA_MATCH_ABORTED_EVENT = 'match/match-aborted';

export const SEKA_GRACE_RESPECT_EVENT = 'game/seka-grace-respect';
export const SEKA_MATCH_WAITING_RECONNECT_EVENT = 'match/match-waiting-reconnect';
export const SEKA_MATCH_RECONNECT_RESUMED_EVENT = 'match/match-reconnect-resumed';
export const SEKA_MATCH_RECONNECT_TIMEOUT_EVENT = 'match/match-reconnect-timeout';

/** Совпадает с server `EVENTS.RESYNC_REQUIRED`. */
export const SEKA_RESYNC_REQUIRED_EVENT = 'system/resync-required';

/**
 * @typedef {{
 *   userId: string,
 *   seatIndex: number,
 *   amount: number,
 * }} SekaAnteContribution
 *
 * @typedef {{
 *   type: 'ANTE_TAKEN',
 *   eventId: string,
 *   handSeq: number,
 *   tableId: string,
 *   ante: number,
 *   contributions: SekaAnteContribution[],
 *   potAfterAnte: number,
 *   at: number,
 * }} SekaAnteTakenEvent
 */

/**
 * @typedef {'FOLD'|'CALL'|'RAISE'|'CHECK'|'LOOK'|'SHOWDOWN'} SekaActionKind
 */

/** @type {Readonly<Record<string, SekaActionKind>>} */
export const SEKA_ACTION = Object.freeze({
  FOLD: 'FOLD',
  CALL: 'CALL',
  RAISE: 'RAISE',
  CHECK: 'CHECK',
  LOOK: 'LOOK',
  SHOWDOWN: 'SHOWDOWN',
});

/**
 * @typedef {{
 *   schemaVersion: number,
 *   table: SekaTablePublic|null,
 *   game: SekaRoundPublic|null,
 *   gameState: SekaRoundPublic|null,
 * }} SekaGameStateSnapshotV1
 */

/**
 * @typedef {'waiting'|'playing'|'finished'} SekaTableLifecycleState
 */

/**
 * @typedef {{
 *   id: string,
 *   name: string,
 *   state: SekaTableLifecycleState,
 *   players: SekaSeatPublic[],
 *   maxPlayers: number,
 * }} SekaTablePublic
 */

/**
 * @typedef {{
 *   userId: string,
 *   username: string,
 *   chips: number,
 *   balance: number,
 *   currentBet: number,
 *   bet: number,
 *   isFolded: boolean,
 *   isSittingOut: boolean,
 *   isDark: boolean,
 *   pendingBuyIn: number,
 *   isBot: boolean,
 *   cards: string[],
 *   avatarUrl?: string,
 * }} SekaSeatPublic
 */

/**
 * @typedef {{ action: string, label: string }} SekaAllowedActionItem
 */

/**
 * Завершённая раздача с дельтами стеков (сервер).
 * @typedef {{
 *   handSeq: number,
 *   pot: number,
 *   potGross?: number,
 *   rakeAmount?: number,
 *   potNet?: number,
 *   recordedAt: number,
 *   earlyFinish?: boolean,
 *   winners: Array<{ userId: string, username: string }>,
 *   seats: Array<{
 *     seatIndex: number,
 *     userId: string,
 *     username: string,
 *     chipsBefore: number,
 *     chipsAfter: number,
 *     delta: number,
 *   }>,
 * }} SekaCompletedHandSummary
 */

/**
 * @typedef {{
 *   seatIndex: number,
 *   userId: string,
 *   username: string,
 *   chips?: number,
 *   entryAmount?: number,
 *   chose: boolean,
 *   canParticipate: boolean,
 *   participate?: boolean|null,
 *   avatarUrl?: string,
 * }} SekaSvarkaCommitSeat
 *
 * @typedef {{
 *   active: boolean,
 *   phase: 'collecting'|'reveal',
 *   effectiveAnte: number,
 *   deadlineAtMs: number,
 *   revealEndsAtMs?: number|null,
 *   seats: SekaSvarkaCommitSeat[],
 * }} SekaSvarkaCommitState
 */

/**
 * `respectGain` и `respectDelta` совпадают; знак может быть отрицательным (поражение).
 *
 * @typedef {{
 *   outcomeId: string,
 *   game: 'seka'|'belot',
 *   mode: 'online'|'local',
 *   userId: string,
 *   result: 'win'|'lose'|'draw',
 *   respectGain: number,
 *   respectDelta?: number,
 *   currencyDelta: { seka: number, bucks: number },
 *   balanceAfter: { seka: number, bucks: number },
 *   progressBefore?: { level: number, xpCurrent: number, xpMax: number, rankName: string },
 *   progress: { level: number, xpCurrent: number, xpMax: number, rankName: string },
 *   road: { previousScore: number, currentScore: number, unlockedMilestones: Array<Record<string, unknown>> },
 *   display: { durationMs: number, style: 'victory'|'defeat'|'neutral' },
 *   at?: number,
 * }} SessionOutcomeEvent
 */

/**
 * Round state. `phase`: preflop, showdown, svarkaBetween; онлайн (будущее): svarkaCommit для opt-in перед анте.
 *
 * @typedef {{
 *   handSeq?: number,
 *   turnSeatIndex: number,
 *   currentPlayer: number,
 *   communityCards: string[],
 *   pot: number,
 *   currentBet: number,
 *   ante?: number,
 *   minRaise?: number,
 *   startingChips?: number,
 *   holeEpoch?: number,
 *   phase: string,
 *   playerCards: Record<string, string[]>,
 *   roundNumber: number,
 *   deck: string[],
 *   deckRemaining?: number,
 *   turnDeadlineAtMs?: number|null,
 *   turnBudgetMs?: number,
 *   handMoves?: Array<Record<string, unknown>>,
 *   svarkaAnteMultiplier?: number,
 *   svarkaCommit?: SekaSvarkaCommitState|null,
 *   gameEnded?: boolean,
 *   completedHandSummaries?: SekaCompletedHandSummary[],
 *   gameHistory?: Array<{
 *     pot: number,
 *     potGross?: number,
 *     rakeAmount?: number,
 *     potNet?: number,
 *     autoShowdownAllInAnte?: boolean,
 *     timestamp?: number,
 *     winner?: { userId?: string, username?: string, name?: string }|null,
 *     winners?: Array<{ userId?: string, username?: string, name?: string }>,
 *     players?: Array<{
 *       id?: string,
 *       userId?: string,
 *       name?: string,
 *       username?: string,
 *       chips?: number,
 *       isFolded?: boolean,
 *       cards?: string[],
 *     }>,
 *     stackDeltas?: Array<{
 *       playerId?: string,
 *       playerName?: string,
 *       startChips?: number,
 *       endChips?: number,
 *       delta?: number,
 *     }>,
 *     actions?: Array<Record<string, unknown>>,
 *   }>,
 *   allowedActions?: SekaAllowedActionItem[],
 *   sidePots?: Array<{ amount?: number }|number>,
 *   nextHandReadyUserIds?: string[],
 *   nextHandReadyDeadlineAtMs?: number|null,
 *   nextHandReadyBudgetMs?: number|null,
 *   sessionOutcomeEvents?: SessionOutcomeEvent[],
 * }} SekaRoundPublic
 */

/**
 * @param {unknown} obj
 * @returns {obj is SekaGameStateSnapshotV1}
 */
export function isSekaGameStateSnapshotV1(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const s = /** @type {SekaGameStateSnapshotV1} */ (obj);
  if (s.schemaVersion !== SEKA_SCHEMA_VERSION) return false;
  return true;
}
